import { inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Observable, of } from 'rxjs';
import { BoelsBaseSite, Alternate, BOProduct } from '@boels/models';
import {
  BoelsAlternatesFacade,
  CategoriesFacade,
  ProductFacade,
  SiteContextFacade,
} from '@boels/shared-state';
import {
  UrlSafePipe,
  LinksService,
  parseUrl,
  RoutingService,
} from '@boels/helpers';
import { filter, map, switchMap, take } from 'rxjs/operators';

@Injectable()
export class BoelsAlternateLinkService {
  private document = inject<Document>(DOCUMENT);
  private boelsLinksService = inject(LinksService);
  private alternateService = inject(BoelsAlternatesFacade);
  private safeUrlPipe = inject(UrlSafePipe);
  private categoriesService = inject(CategoriesFacade);
  private productFacade = inject(ProductFacade);
  private siteContextFacade = inject(SiteContextFacade);

  protected readonly defaultCulture = 'en-nl';
  #routingService = inject(RoutingService);

  private readonly renderer: Renderer2;

  constructor() {
    const rendererFactory = inject(RendererFactory2);

    this.renderer = rendererFactory.createRenderer(null, null);
  }

  resetRegionalHrefLangs(): void {
    const metaLink = this.document.head.querySelectorAll(
      `head > link[rel=alternate]`,
    );
    for (let i = 0; i < metaLink.length; i++) {
      this.renderer.removeChild(this.document.head, metaLink[i]);
    }
  }

  getAlternatesForContentPages(baseSiteUrl: string | undefined): void {
    this.siteContextFacade
      .getAllIsocodes()
      .pipe(take(1))
      .subscribe((cultureList: string[] | null) => {
        const parsedUrl = parseUrl(
          this.#routingService.getRouteProperty('url')?.join('/') || '',
        );
        const path = parsedUrl.path?.endsWith('/find')
          ? parsedUrl.path?.slice(0, -5)
          : parsedUrl.path;
        cultureList?.forEach((culture) => {
          if (culture && baseSiteUrl) {
            const linkElement = this.renderer.createElement('link');
            this.boelsLinksService.setAttributes(this.renderer, linkElement, {
              rel: 'alternate',
              hreflang: this.defaultCulture === culture ? 'x-default' : culture,
              href: this.boelsLinksService.urlBuilder(
                baseSiteUrl,
                culture,
                path ?? '',
              ),
            });
            this.renderer.appendChild(this.document.head, linkElement);
          }
        });
      });
  }

  appendProductListHrefLangs(baseSite: BoelsBaseSite): void {
    const catLevel: string[] =
      parseUrl(
        this.#routingService.getRouteProperty('url')?.join('/') || '',
      ).path?.split('/') || [];
    this.categoriesService
      .getActiveCategory()
      .pipe(
        filter(Boolean),
        switchMap((categoryCode) => {
          if (!categoryCode) return of(null);

          return this.addProductListAlternates(
            baseSite,
            catLevel,
            categoryCode,
          );
        }),
        take(1),
      )
      .subscribe();
  }

  private addProductListAlternates(
    baseSite: BoelsBaseSite,
    catLevel: string[],
    categoryCode: string,
  ): Observable<void> {
    const hreflangs: HTMLElement[] = [];

    return this.alternateService.getByCategoryId(categoryCode).pipe(
      filter(Boolean),
      map((alternateList: Alternate[]) => {
        this.resetRegionalHrefLangs();
        alternateList?.forEach((alternate: Alternate) => {
          const nativeBreadcrumbs = alternate?.nativeBreadcrumbs?.map(
            (element) => this.safeUrlPipe.transform(element),
          );
          const linkElement = this.renderer.createElement('link');
          const isocode = this.boelsLinksService.cultureBuilder(
            alternate.baseSite,
            alternate.language,
          );
          this.boelsLinksService.setAttributes(this.renderer, linkElement, {
            rel: 'alternate',
            hreflang: this.defaultCulture === isocode ? 'x-default' : isocode,
            href: this.boelsLinksService.urlBuilder(
              baseSite.baseSiteUrl as string,
              this.boelsLinksService.cultureBuilder(
                alternate.baseSite,
                alternate.language,
              ),
              ...(nativeBreadcrumbs?.length
                ? nativeBreadcrumbs.filter((crumb) => crumb != undefined)
                : catLevel),
              this.safeUrlPipe.transform(alternate?.nativeName) || '',
              'c',
              categoryCode +
                `${
                  this.#routingService
                    .getRouteProperty('url')
                    ?.join('/')
                    .match(/\?currentPage=([^&]*)/)?.[0] || ''
                }`,
            ),
          });
          hreflangs.push(linkElement);
        });

        this.addHreflangs(hreflangs);
      }),
      take(1),
    );
  }

  appendProductDetailHrefLangs(baseSite: BoelsBaseSite): void {
    const catLevel: string =
      parseUrl(
        this.#routingService.getRouteProperty('url')?.join('/') || '',
      ).path?.split('/')[1] || '';
    this.resetRegionalHrefLangs();
    this.addProductDetailsAlternates(baseSite.baseSiteUrl as string, catLevel);
  }

  addProductDetailsAlternates(baseSiteUrl: string, catLevel: string): void {
    const hreflangs: HTMLElement[] = [];
    this.productFacade
      .getCurrentProduct()
      .pipe(filter(Boolean), take(1))
      .subscribe((currentProduct?: BOProduct) => {
        if (currentProduct?.alternates) {
          const xDefault =
            currentProduct.alternates.find(
              (alternate) =>
                this.boelsLinksService.cultureBuilder(
                  alternate.baseSite,
                  alternate.language,
                ) === this.defaultCulture,
            ) ??
            currentProduct.alternates.find((alternate) =>
              alternate.language.isocode.startsWith('en'),
            );
          currentProduct.alternates.forEach((alternate: Alternate) => {
            const linkElement = this.renderer.createElement('link');
            const isocode = this.boelsLinksService.cultureBuilder(
              alternate.baseSite,
              alternate.language,
            );
            this.boelsLinksService.setAttributes(this.renderer, linkElement, {
              rel: 'alternate',
              hreflang: alternate === xDefault ? 'x-default' : isocode,
              href: this.boelsLinksService.urlBuilder(
                baseSiteUrl,
                isocode,
                this.safeUrlPipe.transform(
                  alternate?.nativeBreadcrumbs?.[0] || catLevel,
                ) || '',
                this.safeUrlPipe.transform(alternate?.nativeName) || '',
                'p',
                currentProduct?.code as string,
              ),
            });
            if (alternate?.nativeName) {
              hreflangs.push(linkElement);
            }
          });
          this.addHreflangs(hreflangs);
        }
      });
  }

  private addHreflangs(hreflangs: HTMLElement[]): void {
    if (hreflangs.length > 1) {
      hreflangs.forEach((htmlElement: HTMLElement) => {
        this.renderer.appendChild(this.document.head, htmlElement);
      });
    }
  }
}
